/**
 * Access-requests admin screen (ADMIN-PR1-a) — fully backed by the live SDK:
 *   - list by status (GET /v1/auth/access-requests/?status=)
 *   - approve with org role (POST /v1/auth/access-requests/{id}/approve {role})
 *   - reject with optional reason (POST /v1/auth/access-requests/{id}/reject {review_reason?})
 */
import { useState, type ReactNode } from "react";
import type { AccessRequestRecord, AccessRequestStatus, OrgRole } from "@oxbo/auth-client";
import { authClient } from "../auth/authClient";
import { errorMessage, useAsync } from "../lib/useAsync";
import { EmptyState, ErrorBanner, PageHeader, Spinner, StatusBadge } from "../components/ui";

const STATUSES: AccessRequestStatus[] = ["pending", "approved", "rejected", "cancelled"];
const ROLES: OrgRole[] = ["owner", "admin", "member"];

function fmtDate(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function AccessRequestsPage(): ReactNode {
  const [status, setStatus] = useState<AccessRequestStatus>("pending");
  const { data, loading, error, reload } = useAsync<AccessRequestRecord[]>(
    () => authClient.listAccessRequests(status),
    [status],
  );
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function onApprove(req: AccessRequestRecord) {
    const role = window.prompt(
      `Approve ${req.organization_name} access for this user — assign org role (owner/admin/member):`,
      "member",
    );
    if (!role) return;
    if (!ROLES.includes(role as OrgRole)) {
      setMutationError(`Invalid role "${role}". Use owner, admin, or member.`);
      return;
    }
    setMutationError(null);
    setBusyId(req.id);
    try {
      await authClient.approveAccessRequest(req.id, { role: role as OrgRole });
      reload();
    } catch (err) {
      setMutationError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  }

  async function onReject(req: AccessRequestRecord) {
    const reason = window.prompt("Reject this request — reason (optional):", "");
    if (reason === null) return; // cancelled
    setMutationError(null);
    setBusyId(req.id);
    try {
      await authClient.rejectAccessRequest(req.id, reason ? { review_reason: reason } : {});
      reload();
    } catch (err) {
      setMutationError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <PageHeader title="Access Requests" />

      <div className="mb-3 flex items-center gap-2">
        <label className="text-sm text-slate-600" htmlFor="ar-status">
          Status
        </label>
        <select
          id="ar-status"
          value={status}
          onChange={(e) => setStatus(e.target.value as AccessRequestStatus)}
          className="rounded border border-slate-300 px-2 py-1 text-sm"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {mutationError && (
        <div className="mb-3">
          <ErrorBanner message={mutationError} />
        </div>
      )}

      {loading ? (
        <Spinner />
      ) : error ? (
        <ErrorBanner message={errorMessage(error)} />
      ) : !data || data.length === 0 ? (
        <EmptyState message={`No ${status} access requests.`} />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-slate-500">
              <tr>
                <th className="px-3 py-2 font-medium">Organization</th>
                <th className="px-3 py-2 font-medium">User</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Message</th>
                <th className="px-3 py-2 font-medium">Requested</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {data.map((req) => (
                <tr key={req.id} className="border-b border-slate-100 last:border-0 align-top">
                  <td className="px-3 py-2">{req.organization_name}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-500" title={req.user_id}>
                    {req.user_id.slice(0, 8)}…
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={req.status} />
                    {req.review_reason && (
                      <div className="mt-1 text-xs text-slate-400">{req.review_reason}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{req.message ?? "—"}</td>
                  <td className="px-3 py-2 text-slate-500">{fmtDate(req.created_at)}</td>
                  <td className="px-3 py-2 text-right">
                    {req.status === "pending" && (
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          disabled={busyId === req.id}
                          onClick={() => void onApprove(req)}
                          className="rounded border border-emerald-300 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          disabled={busyId === req.id}
                          onClick={() => void onReject(req)}
                          className="rounded border border-slate-300 px-2 py-1 text-xs text-red-600 hover:bg-red-50 disabled:opacity-50"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
